import { type FormEvent, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { ApiError } from '../../lib/api';
import { useSession } from '../../stores/session';
import { useAddAlias, useAliases, useRemoveAlias } from '../profile/aliases';

/**
 * The names you release under, on your own settings page.
 *
 * ## Why this is not "add another account"
 *
 * Because a second account would work, and would cost more than it looks. Every
 * rule on this platform that stops somebody paying themselves, boosting their
 * own queue, earning discovery credit off their own catalogue or referring
 * themselves is a comparison of two user ids - two accounts defeat all of them
 * at once. A name is the part people actually want; the account is the part
 * that causes the trouble.
 *
 * ## Removing a name keeps the music
 *
 * Said out loud on the button's own line, because it is the question anybody
 * hesitating over Remove is asking, and the honest answer is reassuring:
 * releases fall back to your own name and nothing is deleted.
 */
export function ArtistNames() {
  const user = useSession((state) => state.user);
  const { data } = useAliases(user !== null);
  const add = useAddAlias();
  const remove = useRemoveAlias();

  const [name, setName] = useState('');

  const aliases = data?.aliases ?? [];

  function submit(event: FormEvent) {
    event.preventDefault();

    const trimmed = name.trim();

    if (trimmed === '' || add.isPending) return;

    add.mutate(trimmed, { onSuccess: () => setName('') });
  }

  /*
   * The server is the authority on both limits - the count and the duplicate -
   * and says so in a field error. This only has to render what it said.
   */
  const trouble =
    add.error instanceof ApiError
      ? (add.error.fieldErrors.name ?? 'That name could not be added.')
      : null;

  return (
    <Card className="p-6" data-artist-names>
      <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">
        Names you release under
      </h2>

      <p className="mb-4 max-w-2xl text-sm leading-relaxed text-soft">
        For a side project, an alias, a duo. When you drop a track you choose which name it goes
        out under, and it appears everywhere that release does. Your profile stays one place, with
        one audience and one set of payouts.
      </p>

      {aliases.length > 0 && (
        <ul className="mb-4 flex flex-col gap-2" data-alias-list>
          {aliases.map((alias) => (
            <li
              key={alias.id}
              data-alias={alias.name}
              className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5"
            >
              <span className="min-w-0">
                <span className="text-sm text-ink">{alias.name}</span>{' '}
                <span className="font-mono text-[11px] text-faint">
                  {alias.tracks === 0
                    ? 'no releases yet'
                    : `${alias.tracks} release${alias.tracks === 1 ? '' : 's'}`}
                </span>
              </span>

              <Button
                variant="ghost"
                onClick={() => remove.mutate(alias.id)}
                loading={remove.isPending && remove.variables === alias.id}
                data-alias-remove={alias.name}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {trouble !== null && (
        <div className="mb-4">
          <Alert tone="error">{trouble}</Alert>
        </div>
      )}

      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[14rem] flex-1">
          <Field
            label="Add a name"
            value={name}
            maxLength={60}
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
            hint="Shown on the releases you choose it for. Nothing else about your account changes."
            data-alias-name
          />
        </div>

        <Button type="submit" variant="secondary" loading={add.isPending} disabled={name.trim() === ''}>
          Add
        </Button>
      </form>

      {aliases.length > 0 && (
        <p className="mt-3 text-[13px] leading-relaxed text-faint">
          Removing a name leaves the music alone. Anything released under it goes back to showing
          your own name.
        </p>
      )}
    </Card>
  );
}
